import React, { useState } from 'react';
import { MdCloudUpload, MdDownload } from 'react-icons/md';
import { RxCross2 } from 'react-icons/rx';
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileVideo } from 'react-icons/fa';
import Loader from './loader';
import { deleteMedia } from '../../services/upload-service';

const AttachmentInput = ({
    fileInputRef,
    handleImageUpload,
    triggerFileInput,
    imagePreview,
    selectedRow,
    handleRemoveImage,
    isUploading,
    accountId,
    attachLabel,
    uploadLabel
}) => {
    const fileType = selectedRow?.expense?.media?.data?.fileType || selectedRow?.expense?.media?.fileType || selectedRow?.media?.fileType;
    const fileName = selectedRow?.expense?.media?.data?.fileName || selectedRow?.expense?.media?.fileName || selectedRow?.media?.fileName;
    const signedUrl = selectedRow?.expense?.media?.data?.signedUrl || selectedRow?.expense?.media?.signedUrl || selectedRow?.media?.signedUrl;
    const signedThumbUrl = selectedRow?.expense?.media?.data?.signedThumbUrl || selectedRow?.expense?.media?.signedThumbUrl || selectedRow?.media?.signedThumbUrl;
    const mediaId = selectedRow?.expense?.media?.data?.mediaId || selectedRow?.expense?.media?.mediaId || selectedRow?.media?.mediaId;


    const isImage = fileType?.startsWith('image/');
    const fileExists = !!(imagePreview || signedUrl || fileName);

    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const getFileIcon = (fileType) => {
        if (fileType?.includes('pdf')) return <FaFilePdf className="text-red-500 text-3xl" />;
        if (fileType?.includes('word') || fileType?.includes('msword')) return <FaFileWord className="text-blue-500 text-3xl" />;
        if (fileType?.includes('excel') || fileType?.includes('spreadsheetml')) return <FaFileExcel className="text-green-500 text-3xl" />;
        if (fileType?.startsWith('video/')) return <FaFileVideo className="text-purple-500 text-3xl" />;
        return <FaFile className="text-gray-500 text-3xl" />;
    };

    const handleDownload = async () => {
        if (!signedUrl) {
            alert('No file available for download.');
            return;
        }

        try {
            const response = await fetch(signedUrl);

            if (!response.ok) {
                throw new Error('Failed to fetch file.');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object after download
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download the file. The URL may be invalid or expired.');
        }
    };


    const handleDelete = async () => {
        if (!mediaId || !accountId) {
            setErrorMessage('No media or account information available for deletion.');
            return;
        }

        try {
            setIsDeleting(true);
            setErrorMessage(null);
            const res = await deleteMedia(accountId, mediaId);
            if (res.status === 204) {
                handleRemoveImage();
            } else {
                setErrorMessage(res.data?.message || 'Failed to delete media.');
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            setErrorMessage('An error occurred while deleting the media.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="mb-1 flex flex-col sm:col-span-2 col-span-1 items-start w-full justify-between">
            <p className="block text-sm font-medium text-gray-700 mb-2">
                {attachLabel || 'Attach Receipt'}
            </p>
            {errorMessage && (
                <p className="text-red-600 text-sm mb-2">{errorMessage}</p>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*,video/*,application/pdf,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                disabled={isUploading || isDeleting || fileExists}
            />
            <div
                onClick={!fileExists && !isUploading && !isDeleting ? triggerFileInput : undefined}
                className={`relative w-full flex justify-center items-center border-2 border-dashed border-blue-400 h-28 rounded-lg bg-bg ${(isUploading || isDeleting || fileExists) ? ' cursor-not-allowed' : 'cursor-pointer'
                    }`}
            >
                {(isUploading || isDeleting) ? (
                    <Loader message={isUploading ? 'Uploading...' : 'Deleting...'} />
                ) : fileExists ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                        {isImage && (imagePreview || signedThumbUrl || signedUrl) ? (
                            <img
                                src={imagePreview || signedThumbUrl || signedUrl}
                                alt="Receipt preview"
                                className="h-28 w-full object-contain p-1"
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-1">
                                {getFileIcon(fileType)}
                                <p className="text-gray-500 text-sm truncate max-w-[200px]">{fileName}</p>
                            </div>
                        )}
                        <div className="absolute top-1 right-1 flex gap-2">
                            {signedUrl && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload();
                                    }}
                                    className={`bg-white rounded-full p-1 shadow-md ${(isUploading || isDeleting) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                                    disabled={isUploading || isDeleting}
                                    title="Download"
                                >
                                    <MdDownload className="text-blue-500 text-lg" />
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete();
                                }}
                                className={`bg-white rounded-full p-1 shadow-md ${(isUploading || isDeleting) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                                disabled={isUploading || isDeleting}
                                title="Remove"
                            >
                                <RxCross2 className="text-red-500 text-lg" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-1">
                        <MdCloudUpload className="text-3xl text-blue-500" />
                        <p className="text-gray-500 text-sm">{uploadLabel || 'Attach a receipt'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttachmentInput;
